import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { buildUrl, ENDPOINTS } from '../config';
import useFieldConfig from '../hooks/useFieldConfig';
import EditElevatorModal from './EditElevatorModal';
import './AdminElevatorManager.css';

function AdminElevatorManager() {
    const { fields, getListFields } = useFieldConfig();
    const [elevators, setElevators] = useState([]);
    const [filteredElevators, setFilteredElevators] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingElevator, setEditingElevator] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        fetchElevators();
    }, []);

    useEffect(() => {
        if (searchTerm.trim()) {
            const keyword = searchTerm.toLowerCase();
            const nextElevators = elevators.filter((elevator) =>
                Object.values(elevator).some((value) =>
                    value && String(value).toLowerCase().includes(keyword)
                )
            );
            setFilteredElevators(nextElevators);
            return;
        }

        setFilteredElevators(elevators);
    }, [elevators, searchTerm]);

    const fetchElevators = async () => {
        try {
            const response = await axios.get(buildUrl(ENDPOINTS.elevators));
            setElevators(response.data);
            setFilteredElevators(response.data);
        } catch (requestError) {
            console.error('Failed to fetch elevators:', requestError);
            alert('获取电梯列表失败，请稍后重试。');
        }
    };

    const handleAddClick = () => {
        setEditingElevator(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (elevator) => {
        setEditingElevator(elevator);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (data) => {
        try {
            if (editingElevator) {
                await axios.put(buildUrl(ENDPOINTS.elevator(editingElevator.id)), data);
                alert('电梯信息已更新。');
            } else {
                try {
                    const check = await axios.get(buildUrl(ENDPOINTS.elevator(data.id)));
                    if (check.data) {
                        alert('该电梯编号已存在，请更换后再提交。');
                        return;
                    }
                } catch (requestError) {
                    if (requestError.response && requestError.response.status !== 404) {
                        throw requestError;
                    }
                }

                await axios.post(buildUrl(ENDPOINTS.elevators), data);
                alert('电梯信息已新增。');
            }

            setIsModalOpen(false);
            fetchElevators();
        } catch (requestError) {
            console.error('Operation failed:', requestError);
            alert(`操作失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const handleDelete = async (id, event) => {
        event.stopPropagation();
        if (!window.confirm(`确定要删除电梯 ${id} 吗？`)) {
            return;
        }

        try {
            await axios.delete(buildUrl(ENDPOINTS.elevator(id)));
            fetchElevators();
        } catch (requestError) {
            console.error('Delete failed:', requestError);
            alert('删除失败，请稍后重试。');
        }
    };

    const toggleSelection = (id) => {
        const nextSelectedIds = new Set(selectedIds);
        if (nextSelectedIds.has(id)) {
            nextSelectedIds.delete(id);
        } else {
            nextSelectedIds.add(id);
        }
        setSelectedIds(nextSelectedIds);
    };

    const handleSelectAll = () => {
        if (filteredElevators.length === 0) {
            return;
        }

        if (selectedIds.size === filteredElevators.length) {
            setSelectedIds(new Set());
            return;
        }

        setSelectedIds(new Set(filteredElevators.map((elevator) => elevator.id)));
    };

    const handleExport = () => {
        if (selectedIds.size === 0) {
            alert('请先选择需要导出的电梯。');
            return;
        }

        const selectedData = elevators.filter((elevator) => selectedIds.has(elevator.id));
        const blob = new Blob([JSON.stringify(selectedData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `elevators_export_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleImportFileChange = (event) => {
        const file = event.target.files?.[0];
        const inputElement = event.target;
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = async (loadEvent) => {
            try {
                const importedData = JSON.parse(loadEvent.target.result);
                if (!Array.isArray(importedData)) {
                    alert('导入文件必须是 JSON 数组。');
                    return;
                }

                if (!window.confirm(`确认导入 ${importedData.length} 条电梯记录吗？`)) {
                    return;
                }

                setIsImporting(true);
                const response = await axios.post(buildUrl(ENDPOINTS.batchImport), importedData);
                alert(`导入完成：成功 ${response.data.successCount} 条，失败 ${response.data.errorCount} 条。`);
                fetchElevators();
            } catch (requestError) {
                console.error('Import failed:', requestError);
                alert(`导入失败：${requestError.message}`);
            } finally {
                setIsImporting(false);
                inputElement.value = '';
            }
        };

        reader.readAsText(file);
    };

    const listFields = getListFields().filter((field) => field.name !== 'id');

    return (
        <section className="manager-layout">
            <div className="manager-overview app-panel">
                <div className="page-heading">
                    <div>
                        <span className="app-eyebrow">电梯资料</span>
                        <h2>电梯列表与批量操作</h2>
                        <p>支持按任意字段搜索、编辑单条记录，也可以进入批量模式后导入或导出数据。</p>
                    </div>
                    <div className="manager-overview__actions">
                        <button type="button" className="app-button app-button--primary" onClick={handleAddClick}>
                            新增电梯
                        </button>
                        <button
                            type="button"
                            className={`app-button ${isSelectionMode ? 'app-button--ghost' : 'app-button--secondary'}`}
                            onClick={() => {
                                setIsSelectionMode((currentValue) => !currentValue);
                                setSelectedIds(new Set());
                            }}
                        >
                            {isSelectionMode ? '退出批量模式' : '进入批量模式'}
                        </button>
                    </div>
                </div>

                <div className="manager-search">
                    <label className="manager-search__field">
                        <span>搜索电梯</span>
                        <input
                            type="text"
                            className="app-input"
                            placeholder="输入编号、地址、使用单位等关键词"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </label>
                    <div className="manager-search__meta">
                        <span className="app-badge">{filteredElevators.length} 条结果</span>
                        <span className="app-badge">{listFields.length} 个列表字段</span>
                    </div>
                </div>
            </div>

            {isSelectionMode && (
                <div className="manager-batch app-panel app-panel--strong">
                    <div className="panel-heading">
                        <h3>批量操作</h3>
                        <p>仅对当前筛选结果中的选中项生效，导入操作会直接调用后端批量接口。</p>
                    </div>

                    <div className="manager-batch__actions">
                        <label className="app-button app-button--warning manager-file-button">
                            导入 JSON
                            <input type="file" accept=".json" hidden onChange={handleImportFileChange} disabled={isImporting} />
                        </label>
                        <button
                            type="button"
                            className="app-button app-button--ghost"
                            onClick={handleExport}
                            disabled={selectedIds.size === 0}
                        >
                            导出已选 ({selectedIds.size})
                        </button>
                        <button type="button" className="app-button app-button--secondary" onClick={handleSelectAll}>
                            {selectedIds.size === filteredElevators.length && filteredElevators.length > 0 ? '取消全选' : '全选当前结果'}
                        </button>
                    </div>
                </div>
            )}

            <div className="manager-table-card app-panel app-panel--strong">
                <div className="manager-table-card__header">
                    <div>
                        <h3>电梯列表</h3>
                        <p>点击行可直接编辑，删除操作在右侧单独保留。</p>
                    </div>
                    {isSelectionMode && <span className="app-badge app-badge--accent">已选 {selectedIds.size} 条</span>}
                </div>

                {filteredElevators.length > 0 ? (
                    <div className="manager-table-wrapper">
                        <table className="manager-table">
                            <thead>
                                <tr>
                                    {isSelectionMode && <th>选择</th>}
                                    <th>电梯编号</th>
                                    {listFields.map((field) => (
                                        <th key={field.name}>{field.label}</th>
                                    ))}
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredElevators.map((elevator) => (
                                    <tr
                                        key={elevator.id}
                                        className={selectedIds.has(elevator.id) ? 'manager-table__row--selected' : ''}
                                        onClick={() => (isSelectionMode ? toggleSelection(elevator.id) : handleEditClick(elevator))}
                                    >
                                        {isSelectionMode && (
                                            <td
                                                className="manager-table__checkbox"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(elevator.id)}
                                                    onChange={() => toggleSelection(elevator.id)}
                                                />
                                            </td>
                                        )}
                                        <td className="manager-table__id">{elevator.id}</td>
                                        {listFields.map((field) => (
                                            <td key={field.name}>{elevator[field.name] || '—'}</td>
                                        ))}
                                        <td
                                            className="manager-table__actions"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                className="app-button app-button--ghost app-button--small"
                                                onClick={() => handleEditClick(elevator)}
                                            >
                                                编辑
                                            </button>
                                            <button
                                                type="button"
                                                className="app-button app-button--danger app-button--small"
                                                onClick={(event) => handleDelete(elevator.id, event)}
                                            >
                                                删除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="app-empty-state manager-empty-state">
                        <h3>当前没有匹配的电梯</h3>
                        <p>可以更换搜索词，或者先新增一条电梯记录。</p>
                    </div>
                )}
            </div>

            <EditElevatorModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                fields={fields}
                initialData={editingElevator}
                isEditing={Boolean(editingElevator)}
                onSubmit={handleModalSubmit}
            />
        </section>
    );
}

export default AdminElevatorManager;
